import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useEffect } from "react";
import { getAllPostsSorted } from "@/content/blog/posts";
import { PageNav, PageFooter } from "@/components/blog/PageChrome";
import { EASE, FADE_UP_20, FADE_UP_30, VISIBLE, TITLE_TRANSITION } from "@/lib/motion";

const dateFmt = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

const VIEWPORT_ONCE = { once: true, margin: "-80px" };
const SUBTITLE_TRANSITION = { duration: 0.9, delay: 0.2 };
const HAIRLINE_TRANSITION = { duration: 1.1, delay: 0.4, ease: EASE };

const BlogListItem = ({ post: p, index: i }) => (
  <motion.article
    initial={FADE_UP_30}
    whileInView={VISIBLE}
    viewport={VIEWPORT_ONCE}
    transition={{ duration: 0.8, delay: i * 0.06, ease: EASE }}
    className="group"
    data-testid={`blog-item-${i}`}
  >
    <Link
      to={`/blog/${p.slug}`}
      data-testid={`blog-item-link-${i}`}
      className="grid grid-cols-12 gap-4 md:gap-8 py-10 md:py-14 hover:bg-matte/40 transition-colors -mx-6 md:-mx-10 px-6 md:px-10"
    >
      <div className="col-span-12 md:col-span-3">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-laser">
          {dateFmt(p.date)}
        </div>
        <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-steel">
          {p.readTime}
        </div>
      </div>
      <div className="col-span-12 md:col-span-8">
        <h2 className="font-serif font-light text-3xl md:text-5xl text-ink leading-[1.05] tracking-tight group-hover:text-laser transition-colors">
          {p.title}
        </h2>
        <p className="mt-4 font-sans text-base md:text-lg text-steel max-w-2xl leading-relaxed">
          {p.excerpt}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {p.tags.map((t) => (
            <span key={t} className="font-mono text-[10px] tracking-[0.24em] uppercase text-steel border border-stroke px-3 py-1.5">
              {t}
            </span>
          ))}
        </div>
      </div>
      <div className="col-span-12 md:col-span-1 md:justify-self-end self-center">
        <ArrowUpRight size={28} strokeWidth={1} className="text-steel group-hover:text-laser group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
      </div>
    </Link>
  </motion.article>
);

export default function Blog() {
  const posts = getAllPostsSorted();

  useEffect(() => {
    document.title = "Journal — Himanshu Oberoi";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="relative min-h-screen bg-obsidian text-white overflow-x-hidden" data-testid="blog-root">
      <div className="grain" aria-hidden />
      <PageNav />

      <section className="container-x pt-32 md:pt-40 pb-16" data-testid="blog-hero">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="eyebrow">/// Journal</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <motion.h1
              initial={FADE_UP_30}
              animate={VISIBLE}
              transition={TITLE_TRANSITION}
              className="font-serif font-light text-5xl md:text-7xl lg:text-8xl leading-[0.94] tracking-tight text-ink"
              data-testid="blog-title"
            >
              Field notes from the <span className="text-laser italic">funnel.</span>
            </motion.h1>
            <motion.p
              initial={FADE_UP_20}
              animate={VISIBLE}
              transition={SUBTITLE_TRANSITION}
              className="mt-6 font-sans text-lg text-steel max-w-2xl leading-relaxed"
            >
              Unfiltered notes on performance marketing, attribution, and building growth systems that
              survive first contact with reality.
            </motion.p>
          </div>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={HAIRLINE_TRANSITION}
          className="mt-16 md:mt-20 origin-left hairline"
        />
      </section>

      <section className="container-x pb-24 md:pb-40" data-testid="blog-list">
        {posts.length === 0 ? (
          <div className="border border-stroke bg-matte p-10 md:p-16 text-center" data-testid="blog-empty">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-laser mb-4">
              /// The press is warming up
            </p>
            <h2 className="font-serif font-light text-3xl md:text-4xl text-ink leading-tight">
              First edition drops soon.
            </h2>
            <p className="mt-4 font-sans text-steel max-w-lg mx-auto">
              I'm drafting the opening set now. Want a heads-up when they land? Reach out below.
            </p>
            <Link
              to="/#contact"
              data-testid="blog-empty-cta"
              className="btn-sweep btn-sweep-primary inline-flex items-center gap-3 border border-laser text-ink px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase mt-8"
            >
              Get notified
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-stroke border-y border-stroke" data-testid="blog-list-items">
            {posts.map((p, i) => (
              <BlogListItem key={p.slug} post={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <PageFooter />
    </main>
  );
}
